import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency } from '../utils/currency';
import { fetchCaseTrustLedger, downloadCaseTrustLedgerPdf } from '../api';

export default function TrustLedgerPanel({ authAxios, showFormMessage, caseId }) {
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const data = await fetchCaseTrustLedger(authAxios, caseId);
      setLedger(data);
    } catch {
      showFormMessage?.('error', 'Failed to load trust ledger.');
    } finally {
      setLoading(false);
    }
  }, [authAxios, caseId, showFormMessage]);

  useEffect(() => { load(); }, [load]);

  const handleDownloadPdf = async () => {
    try {
      const resp = await downloadCaseTrustLedgerPdf(authAxios, caseId);
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `TrustLedger_${ledger?.case_number || caseId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showFormMessage?.('error', 'Failed to download trust ledger PDF.');
    }
  };

  const fmt = (v) => (v != null ? formatCurrency(v) : '');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <svg className="animate-spin h-6 w-6 mr-2" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Loading trust ledger...
      </div>
    );
  }

  if (!ledger) return null;

  const { transactions = [], closing_balance, case_number, case_title, client } = ledger;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Per-Matter Trust Ledger</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {case_number} — {client}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
          >
            Refresh
          </button>
          <button
            onClick={handleDownloadPdf}
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            Download PDF
          </button>
        </div>
      </div>

      {/* Ledger Table */}
      {transactions.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm border rounded-xl bg-gray-50">
          No trust transactions recorded for this matter.
        </div>
      ) : (
        <div className="overflow-x-auto border rounded-xl">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2.5">Date</th>
                <th className="px-4 py-2.5">Description</th>
                <th className="px-4 py-2.5 text-right">Receipt</th>
                <th className="px-4 py-2.5 text-right">Payment</th>
                <th className="px-4 py-2.5 text-right">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {transactions.map(tx => (
                <tr key={tx.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{tx.date}</td>
                  <td className="px-4 py-2 text-gray-800">{tx.description}</td>
                  <td className="px-4 py-2 text-right text-green-700 font-medium">
                    {tx.receipt != null ? fmt(tx.receipt) : ''}
                  </td>
                  <td className="px-4 py-2 text-right text-red-600 font-medium">
                    {tx.payment != null ? fmt(tx.payment) : ''}
                  </td>
                  <td className="px-4 py-2 text-right font-semibold text-gray-900">
                    {fmt(tx.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 font-semibold">
                <td colSpan={4} className="px-4 py-2.5 text-right text-gray-700">
                  Closing Balance
                </td>
                <td className="px-4 py-2.5 text-right text-gray-900">
                  {fmt(closing_balance)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
